/**
 * Axios instance for the public site. Talks to the backend via VITE_API_BASE_URL
 * (proxied to :4000 in dev). Sends cookies so refresh-token flows work, unwraps the
 * { data, meta, error } envelope, and normalises errors into a consistent shape.
 */
import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: attach a client request id for traceability ──────────────────────
apiClient.interceptors.request.use((config) => {
  config.metadata = { start: Date.now() };
  return config;
});

// ── Response: unwrap envelope, normalise errors ───────────────────────────────
apiClient.interceptors.response.use(
  (response) => {
    // Return the whole envelope so callers can read meta (pagination cursors).
    return response.data;
  },
  (error) => {
    const envelope = error.response?.data;
    const normalised = {
      status: error.response?.status ?? 0,
      code: envelope?.error?.code || (error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR'),
      message:
        envelope?.error?.message ||
        (error.response ? 'Request failed' : 'Could not reach the server. Check your connection.'),
      details: envelope?.error?.details,
      requestId: envelope?.meta?.requestId,
    };
    return Promise.reject(normalised);
  },
);

/** Convenience helpers returning just `data` from the envelope. */
export const api = {
  get: async (url, config) => (await apiClient.get(url, config)).data,
  getEnvelope: (url, config) => apiClient.get(url, config),
  post: async (url, body, config) => (await apiClient.post(url, body, config)).data,
  patch: async (url, body, config) => (await apiClient.patch(url, body, config)).data,
  put: async (url, body, config) => (await apiClient.put(url, body, config)).data,
  del: async (url, config) => (await apiClient.delete(url, config)).data,
};

export default apiClient;
